import React, { useEffect, useState } from "react";
import { useLocation , NavLink} from 'react-router-dom';
import config from "react-global-configuration";
import CryptoJS from "crypto-js";
import Header from "./../../Header";
import moment from "moment";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from "dompurify";
import 'highlight.js/styles/atom-one-dark.css';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {ThinkingProcessTimeline} from "./ThinkingProcessTimeline";
import { BsArrowLeft } from "react-icons/bs";

import EditorNew from "../Editor/EditorNew";

const CodeBlock = ({ node, inline, className, children, ...props }) => {
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';

  try {
    return !inline && match ? (
      <SyntaxHighlighter
        language={language}
        style={oneLight}
        PreTag="div"
        wrapLongLines={true}
        //preTagProps={{ className: 'alie-code-wrapper' }}
        customStyle={{
          background: 'transparent', // ✅ override theme background
          borderRadius: '6px',
          fontSize: '14px',
          padding: '1rem'
        }}
        className={'alie-code-wrapper'} // ✅ CORRECT
        {...props}
      >
        {String(children).replace(/\n$/, '')}
      </SyntaxHighlighter>
    ) : (
      <code className={className} {...props}>
        {children}
      </code>
    );
  } catch (err) {
    console.warn(`⚠️ Unknown language: ${language}`, err);
    return (
      <pre className="hljs">
        <code>{children}</code>
      </pre>
    );
  }
};

const opennewTab = {
    a: ({ node, ...props }) => (
        <a {...props} target="_blank" rel="noopener noreferrer" />
    )
};

const RESPONSE_HTML_STYLE = {
  color: "#1f2937",
  fontSize: 14,
  lineHeight: 1.7,
  wordBreak: "break-word",
};


const rowStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 16,
  marginBottom: 12,
};

const cellStyle = {
  minWidth: 0,
};

const labelStyle = {
  fontSize: 12,
  color: "#777",
  marginBottom: 4,
};

const valueStyle = {
  fontSize: 14,
  color: "#222",
};

const tagStyle = {
  display: "inline-block",
  padding: "4px 10px",
  marginRight: 6,
  marginBottom: 6,
  borderRadius: 12,
  background: "#f1f3f5",
  fontSize: 12,
};

const dividerStyle = {
  height: 1,
  backgroundColor: "#eee",
  margin: "16px 0",
};

const fullWidthStyle = {
  marginTop: 12,
};

const FLAG_LABELS = [
  ["was_down_voted", "Downvoted"],
  ["survey_rating_bad", "Bad Survey"],
  ["low_confidence", "Low Confidence"],
  ["web_search_used", "Web Search"],
  ["flagged", "Flagged"],
];

const EvalDetailPage = (props) => {

    const location = useLocation();

    // Safe access to state (v5 uses location.state same as v6)
    const returnPage = (location.state && location.state.page) ? location.state.page : 1;


    const evalId =
      props &&
      props.match &&
      props.match.params &&
      props.match.params.id
        ? props.match.params.id
        : null;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [interactionData, setInteractionData] = useState(null);
    const [interactionLoading, setInteractionLoading] = useState(false);
    const [interactionError, setInteractionError] = useState(null);

    // 1. State to store the note content coming from the editor
    const [noteData, setNoteData] = useState(null);
    
    // 2. State to handle the loading status of the save button
    const [isSaving, setIsSaving] = useState(false);

    const [errorMsg, setErrorMsg] = useState(""); // State for validation error

    const [currentUserUUID, setCurrentUserUUID] = useState(null);

    // 3. Mock Users Data (Replace with your actual user data source)
    const [userList, setUserList] = useState({});

    const [evalNotes, setEvalNotes] = useState({});

    const [editSessionId, setEditSessionId] = useState(0);

    useEffect(() => {
        if(document.title){
            document.title = "Alie | AI Eval";
        }

        if (evalId) {
            fetchEvalDetail();
        }
    }, [evalId]);

    const looksLikeHtml = (value) => {
        if (!value || typeof value !== "string") {
            return false;
        }

        return /<\/?[a-z][\s\S]*>/i.test(value);
    };

    const sanitizeHtmlContent = (value) => {
        if (!value || typeof value !== "string") {
            return "";
        }

        return DOMPurify.sanitize(value, {
            ALLOWED_URI_REGEXP: /^https?:\/\//i,
        });
    };

    const fetchEvalDetail = async () => {

        try {

            setLoading(true);

            setError(null);

            const response = await fetch(
                `${process.env.REACT_APP_ALIE_API_URL}/alie/v1/evaluation/detail?eval_id=${encodeURIComponent(evalId)}`
            );

            if (!response.ok) {
                throw new Error("API request failed");
            }

            const result = await response.json();

            if (!result || !result.data) {
                throw new Error("Evaluation not found");
            }

            setData(result.data);

            getEvalNotes(evalId);

        } catch (err) {
            setError("Failed to load evaluation details");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {

        if (!data || !data.interaction_id) {
            setInteractionData(null);
            setInteractionError(null);
            return;
        }

        const fetchInteraction = async () => {
            try {

                setInteractionLoading(true);
                setInteractionError(null);

                const response = await fetch(
                    `${process.env.REACT_APP_ALIE_API_URL}/alie/v1/interaction/detail?interaction_id=${encodeURIComponent(data.interaction_id)}`
                );

                if (!response.ok) {
                    throw new Error("Interaction API failed");
                }

                const result = await response.json();

                setInteractionData(Array.isArray(result.data) && result.data.length > 0 ? result.data : null);

            } catch (err) {
                setInteractionError("Failed to load interaction details");
                setInteractionData(null);
            } finally {
                setInteractionLoading(false);
            }
        };

        fetchInteraction();
        
    }, [data]);

    const decrypt = (findresponse, flg) => {
        var key = CryptoJS.enc.Hex.parse(config.get("enc_key"));
        var iv = CryptoJS.enc.Hex.parse(config.get("enc_iv"));
        var res = flg ? findresponse.encdata : findresponse;
        var bytes = CryptoJS.AES.decrypt(res, key, {
            iv,
        });
        var plaintext = bytes.toString(CryptoJS.enc.Utf8);
        return JSON.parse(plaintext);
    };


    useEffect(() => {
        const fetchUsers = async () => {
        try {
            const apiUrl = process.env.REACT_APP_CRM + "/user-list";
            
            // Changed to POST method
            const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({}) // Sending empty body if API expects one
            });

            const result = await response.json();

            if (result.status === "success" && result.data) {
                setUserList(result.data);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
        }
        };

        fetchUsers();
    }, []);

    useEffect(() => {
        if (Object.keys(userList).length > 0) {
            const rawEncData = localStorage.getItem("encdata");
        
            if (rawEncData) {
                const userdetails = decrypt(rawEncData, 0);
                const emp_id = parseInt(userdetails.user_id);
                let foundUuid = null;

                // Loop through userList to find matching emp_id
                for (const key in userList) {
                if (userList.hasOwnProperty(key)) {
                    const user = userList[key];
                    if (user.emp_id === emp_id) {
                    foundUuid = user.user_uuid;
                    break;
                    }
                }
                }
                
                if (foundUuid) {
                    console.log("Logged in User UUID found:", foundUuid);
                    setCurrentUserUUID(foundUuid);
                } else {
                    console.warn("Could not find matching user_uuid for emp_id:", emp_id);
                }
            }
        }
    }, [userList]); // Runs whenever userList updates

    // 4. Callback to receive data as the user types
    const handleEditorChange = (formData) => {
        // formData looks like: { note_content: { text: "...", html_content: "..." }, mentioned_data: [...] }
        console.log("Editor Content Updated:", formData);

        if (errorMsg) setErrorMsg("");
        setNoteData(formData); 
    };

    // 5. Function to handle the "Add Note" click
    const handleSaveNote = async () => {

        console.log("Saving Note with data:", noteData);

        if (isSaving || !noteData) return;

        const content = noteData && noteData.note_content.html_content
        ? noteData.note_content.html_content
        : null;

        const draft_js_content = noteData && noteData.note_content.draft_js_content
        ? noteData.note_content.draft_js_content
        : null;

        console.log("Note Content to Save:", content);

        if (!content || content.trim().length === 0) {
            setErrorMsg("Please enter a note before saving.");
            return;
        }

        setErrorMsg("");

        setIsSaving(true);

        try {
            // 2. Prepare the Payload
            const payload = {
                attribute_set_key: "eval",
                app_token: process.env.REACT_APP_tattle_token,
                customer_tattle_uuid: Number(evalId), // The eval ID this note is associated with
                user_uuid: currentUserUUID, // The ID of the record/person this note belongs to
                note_content: content,
                note_content_json: draft_js_content, // Passing the raw object directly
                mention: noteData.mentioned_data || [],
                note_type: "internal",
                attribute_set_type: 20
            };

            // 3. Call the API
            const response = await fetch(`${process.env.REACT_APP_tattle_api}/note/create`, {
                method: "POST",
                headers: {
                "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            if (result.status === "success" || result.code === 200) {

                console.log("Note saved successfully!");
                // Optional: Reset editor or show success message here
            } else {
                //setErrorMsg("Failed to save note: " + (result.message || "Unknown error"));
            }

            console.log("API Response:", result);

            // 4. Refresh the Notes List
            getEvalNotes(evalId);

            setNoteData(null);

            setEditSessionId(prev => prev + 1);

        } catch (error) {
            console.error("API Error:", error);

            setNoteData(null);

            getEvalNotes(evalId);

            setEditSessionId(prev => prev + 1);

           // setErrorMsg("Something went wrong while saving.");
        } finally {
            setIsSaving(false);
        }

    };


    const getEvalNotes = (evalId) => {
		try {
			let requests = {
				customer_tattle_uuid: Number(evalId),
				attribute_set_key: "eval",
				app_token: process.env.REACT_APP_tattle_token,
				attribute_set_type: 20,
			};

			const output = fetch(`${process.env.REACT_APP_tattle_api}/notes/list`, {
				method: "POST",
				body: JSON.stringify(requests),
			});

			output
				.then((i) => {
					return i.json();
				})
				.then((out) => {
					setEvalNotes(out);
				});
		} catch (err) {
			console.log(err.message);
		}
	};

    const convertNoteList = (data) => {
		const linkRegex = /<a[^>]*>.*?<\/a>|((http:\/\/|https:\/\/)[^\s]+)/gi;

		return data.replace(linkRegex, (match, group1) => {
			if (group1) {
				return `<a href="${group1.replace(/<p>/g, "").replace(/<\/p>/g, "")}" target="_blank">${group1}</a>`;
			} else {
				return match;
			}
		});
	};

    const getFlagLabels = (flags) => {
        if (!flags || typeof flags !== "object") {
            return [];
        }

        return FLAG_LABELS.filter(([key]) => Boolean(flags[key])).map(([, label]) => label);
    };

    const buildSourceLink = (evaluation) => {
        if (evaluation && evaluation.customer_tattle_uuid) {
            return `/chat/${evaluation.customer_tattle_uuid}${evaluation.interaction_id ? `#${evaluation.interaction_id}` : ""}`;
        }
        return `/alie?conversation_id=${evaluation && evaluation.conversation_id ? evaluation.conversation_id : ""}${evaluation && evaluation.interaction_id ? `#${evaluation.interaction_id}` : ""}`;
    };

    const flagLabels = getFlagLabels(data && data.flags);
    const responsePreview =
        (data && data.assistant_answer_preview)
        || ((data && data.events) ? data.events.map(item => item && item.assistant_answer_preview).find(Boolean) : "")
        || "";
    const citations = data && Array.isArray(data.citations) ? data.citations : [];
    const triggerTypes = data && Array.isArray(data.trigger_types) ? data.trigger_types : [];

    return (
        <div>
        <Header brecum_name="AI" brecum_page="Eval Details" />
        <div class="right_col">
            <div class="ticket_list">

                {loading && (
                <div className="loader-new"></div>
                )}

                {/* Error */}
                {!loading && error && (
                <div className="container text-center" style={{ marginTop: 50 }}>
                    <span className="text-danger">{error}</span>
                </div>
                )}

                {!loading && !error && data && (

                <div className="intranet_body">
                    <div class="title mob er_ids">
                        <div class="er_id">
                            <span class="er">Eval ID : {evalId}</span>
                        </div>
                         <div class="edit pull-right">

                            
                            <NavLink
                                to={{
                                    pathname: "/eval",
                                    state: { 
                                        page: returnPage // Pass it back so the listing knows where to start
                                    }
                                }}
                            >
                                <button
                                    className="back-button"
                                    style={{
                                        margin: ".5rem .5rem",
                                        padding: ".1em",
                                        background: "none",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <BsArrowLeft
                                        style={{
                                            fontSize: "18px",
                                        }}
                                    />
                                    &nbsp; Back to Evals
                                </button>
                            </NavLink>
                        </div>
                    </div>
                   
                    <div style={{ padding: '1em' }}>
                        <div class="request_details er row margin_top_less">
                            <div class="col-md-7">
                                <div style={{ padding: "1.5rem" }}>
                                    <div style={{ marginBottom: 20 }}>
                                        <strong>User Query:</strong>
                                        <div style={{ marginTop: 5 }}>
                                        {data.user_query ? data.user_query : <span className="text-muted">—</span>}
                                        </div>
                                    </div>
                                    <div style={{ marginBottom: 20 }}>
                                        <strong>Response:</strong>
                                        <div style={{ marginTop: 5 }}>

                                            <div className="markdown-body">
                                                {responsePreview ? (
                                                    looksLikeHtml(responsePreview) ? (
                                                        <div
                                                            style={RESPONSE_HTML_STYLE}
                                                            dangerouslySetInnerHTML={{ __html: sanitizeHtmlContent(responsePreview) }}
                                                        />
                                                    ) : (
                                                        <ReactMarkdown
                                                        remarkPlugins={[remarkGfm]}
                                                        components={{
                                                            code: CodeBlock,
                                                            ...opennewTab
                                                            }}
                                                        >
                                                        {responsePreview}
                                                        </ReactMarkdown>
                                                    )
                                                ) : (
                                                    <span className="text-muted">—</span>
                                                )}
                                            </div>
                                        
                                        </div>

                                    </div>

                                    {/* <div style={{ marginBottom: 20 }}>
                                    <strong>Flags:</strong>{' '}
                                    {data.flags && data.flags.length > 0 ? (
                                        data.flags.map((flag, index) => (
                                        <span
                                            key={index}
                                            className="label label-primary"
                                            style={{ marginRight: 6, marginBottom: 6, display: 'inline-block' }}
                                        >
                                            {flag}
                                        </span>
                                        ))
                                    ) : (
                                        <span className="text-muted">No flags</span>
                                    )}
                                    </div>

                                    <div style={{ marginBottom: 20 }}>
                                    <strong>Query Intent:</strong>{' '}
                                    {data.intent ? data.intent : <span className="text-muted">N/A</span>}
                                    </div> */}

                                    <div style={{ marginBottom: 20 }}>
                                        <div
                                            style={{
                                            border: "1px solid #ddd",
                                            borderRadius: 6,
                                            backgroundColor: "#fff",
                                            }}
                                        >
                                            {/* Header */}
                                            <div
                                            style={{
                                                padding: "10px 15px",
                                                backgroundColor: "#f5f5f5",
                                                borderBottom: "1px solid #ddd",
                                                borderRadius: "6px 6px 0 0",
                                                fontWeight: 600,
                                            }}
                                            >
                                            Chat Details
                                            </div>

                                            {/* Body */}
                                            <div style={{ padding: 16 }}>
                                            {/* Row 1 */}
                                            <div style={rowStyle}>
                                                <div style={cellStyle}>
                                                <div style={labelStyle}>Flags</div>
                                                <div style={valueStyle}>
                                                    {flagLabels.length > 0 ? (
                                                    flagLabels.map((flag, index) => (
                                                        <span key={index} style={tagStyle}>
                                                        {flag}
                                                        </span>
                                                    ))
                                                    ) : (
                                                    <span className="text-muted">No flags</span>
                                                    )}
                                                </div>
                                                </div>

                                                <div style={cellStyle}>
                                                <div style={labelStyle}>Query Intent</div>
                                                <div style={valueStyle}>
                                                    {data && data.intent_code ? data.intent_code : (
                                                    <span className="text-muted">N/A</span>
                                                    )}
                                                </div>
                                                </div>
                                            </div>

                                            {/* Row 2 */}
                                            <div style={rowStyle}>
                                                <div style={cellStyle}>
                                                <div style={labelStyle}>Time</div>
                                                <div style={valueStyle}>
                                                    {data && data.date_of_eval ? (
                                                    moment(data.date_of_eval).format("DD MMM YYYY, hh:mm A")
                                                    ) : (
                                                    <span className="text-muted">N/A</span>
                                                    )}
                                                </div>
                                                </div>

                                                <div style={cellStyle}>
                                                <div style={labelStyle}>Agent</div>
                                                <div style={valueStyle}>
                                                    {data && (data.agent_name || data.agent_id)
                                                    ? (data.agent_name || data.agent_id)
                                                    : <span className="text-muted">N/A</span>}
                                                </div>
                                                </div>
                                            </div>

                                            {/* Divider */}
                                            <div style={dividerStyle} />

                                            {/* Row 3 */}
                                            <div style={rowStyle}>
                                                <div style={cellStyle}>
                                                <div style={labelStyle}>Customer Tattle UUID</div>
                                                <div style={valueStyle}>
                                                    {data && data.customer_tattle_uuid
                                                    ? data.customer_tattle_uuid
                                                    : <span className="text-muted">N/A</span>}
                                                </div>
                                                </div>

                                                <div style={cellStyle}>
                                                <div style={labelStyle}>Trigger Types</div>
                                                <div style={valueStyle}>
                                                    {triggerTypes.length > 0
                                                    ? triggerTypes.join(", ")
                                                    : <span className="text-muted">N/A</span>}
                                                </div>
                                                </div>
                                            </div>

                                            {/* Full width Chat Link */}
                                            <div style={fullWidthStyle}>
                                                <div style={labelStyle}>Chat Link</div>
                                                <div style={valueStyle}>
                                                {data && (data.customer_tattle_uuid || data.conversation_id) ? (

                                                    <a
                                                        href={buildSourceLink(data)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="important-link"
                                                    >
                                                         {data.customer_tattle_uuid ? `View Chat #${data.customer_tattle_uuid}` : "View Conversation"}
                                                    </a>

                                                ) : (
                                                    <span className="text-muted">N/A</span>
                                                )}
                                                </div>
                                            </div>

                                            <div style={fullWidthStyle}>
                                                <div style={labelStyle}>Citations</div>
                                                <div style={valueStyle}>
                                                {citations.length > 0 ? (
                                                    <div>
                                                        {citations.map((item, index) => (
                                                            <div key={`${item.source_url || "citation"}-${index}`} style={{ marginBottom: 8 }}>
                                                                <a
                                                                    href={item.source_url || "#"}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="important-link"
                                                                >
                                                                    {item.page_title || item.source_url}
                                                                </a>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted">No citations</span>
                                                )}
                                                </div>
                                            </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* <div style={{ marginBottom: 20 }}>
                                    <div style={{ 
                                        border: '1px solid #ddd', 
                                        borderRadius: '4px',
                                        backgroundColor: '#fff'
                                    }}>
                                        <div style={{ 
                                        padding: '10px 15px', 
                                        backgroundColor: '#f5f5f5', 
                                        borderBottom: '1px solid #ddd',
                                        borderRadius: '3px 3px 0 0'
                                        }}>
                                        <strong>Query Information</strong>
                                        </div>
                                        <div style={{ padding: '15px' }}>
                                        <table className="table table-bordered" style={{ marginBottom: 0 }}>
                                            <tbody>
                                            <tr>
                                                <td style={{ width: '30%' }}><strong>User Query</strong></td>
                                                <td>{data.user_query ? data.user_query : <span className="text-muted">—</span>}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Response</strong></td>
                                                <td>

                                                    <div className="markdown-body">
                                                        <ReactMarkdown
                                                        remarkPlugins={[remarkGfm]}
                                                        components={{
                                                            code: CodeBlock,
                                                            ...opennewTab
                                                            }}
                                                        >
                                                        {data.response_preview ? data.response_preview : <span className="text-muted">—</span>}
                                                        </ReactMarkdown>
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td><strong>Flags</strong></td>
                                                <td>
                                                {data.flags && data.flags.length > 0 ? (
                                                    data.flags.map((flag, index) => (
                                                    <span
                                                        key={index}
                                                        className="label label-primary"
                                                        style={{ marginRight: 6, marginBottom: 6, display: 'inline-block' }}
                                                    >
                                                        {flag}
                                                    </span>
                                                    ))
                                                ) : (
                                                    <span className="text-muted">No flags</span>
                                                )}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td><strong>Query Intent</strong></td>
                                                <td>{data.intent ? data.intent : <span className="text-muted">N/A</span>}</td>
                                            </tr>
                                            </tbody>
                                        </table>
                                        </div>
                                    </div>
                                    </div>

                                    <div style={{ marginBottom: 20 }}>
                                    <div style={{ 
                                        border: '1px solid #ddd', 
                                        borderRadius: '4px',
                                        backgroundColor: '#fff'
                                    }}>
                                        <div style={{ 
                                        padding: '10px 15px', 
                                        backgroundColor: '#f5f5f5', 
                                        borderBottom: '1px solid #ddd',
                                        borderRadius: '3px 3px 0 0'
                                        }}>
                                        <strong>Chat Details</strong>
                                        </div>
                                        <div style={{ padding: '15px' }}>
                                        {data.eval_source ? (
                                            <table className="table table-bordered" style={{ marginBottom: 0 }}>
                                            <tbody>
                                                <tr>
                                                <td style={{ width: '30%' }}><strong>Lead Name</strong></td>
                                                <td>{data.eval_source.name}</td>
                                                </tr>
                                                <tr>
                                                <td><strong>Agent</strong></td>
                                                <td>{data.eval_source.agent}</td>
                                                </tr>
                                                <tr>
                                                <td><strong>Email</strong></td>
                                                <td>{data.eval_source.email}</td>
                                                </tr>
                                                <tr>
                                                <td><strong>Source Type</strong></td>
                                                <td>{data.eval_source.source_type}</td>
                                                </tr>
                                                <tr>
                                                <td><strong>Chat Link</strong></td>
                                                <td>
                                                    <a href={`/chat/${data.eval_source.customer_tattle_uuid}`} target="_blank" rel="noopener noreferrer">
                                                    View Chat #{data.eval_source.customer_tattle_uuid}
                                                    </a>
                                                </td>
                                                </tr>
                                            </tbody>
                                            </table>
                                        ) : (
                                            <span className="text-muted">No chat details available</span>
                                        )}
                                        </div>
                                    </div>

                                    </div> */}

                                    {/* {interactionData && (
                                    <div className="interaction_box">
                                        <ThinkingProcessTable
                                            data={interactionData}
                                        />
                                    </div>
                                    )} */}

                                    <style>
                                        {`
                                        /* Target the wrapper first to ensure we only style THIS editor */
                                        .ai-eval-container .save-btn {
                                            background-color: #2c82e3; /* Your preferred color */
                                            color: white;
                                            padding: 8px 16px;
                                            border: none;
                                            border-radius: 4px;
                                            font-size: 14px;
                                            cursor: pointer;
                                            transition: all 0.3s ease;
                                        }

                                        .ai-eval-container .save-btn:hover {
                                            background-color: #0050b5;
                                            box-shadow: 0 2px 5px rgba(0,0,0,0.2);
                                        }
                                        
                                        /* Style the "Adding..." state */
                                        .ai-eval-container .save-btn:disabled {
                                            opacity: 0.7;
                                            cursor: not-allowed;
                                        }

                                        .ai-eval-container .editorToolbar {
                                            margin-top:1rem;
                                        }

                                        `}
                                    </style>
                                    <div className="ai-eval-container">
                                        <div id="exTab1" class="container title">
                                            <ul class="nav nav-pills">
                                                <li class="active"><a href="#note" data-toggle="tab">Notes</a></li>
                                            </ul>
                                        </div>
                                        <div class="tab-content customer-tab clearfix">
                                            <div className="tab-pane active" id="note">
                                                <div class="row">
                                                    <div className="form-section ckeditor">
                                                        

                                                        {Object.keys(userList).length > 0 ? (
                                                            
                                                            <EditorNew 
                                                                users={userList}               // Pass available users for @mentions
                                                                field_name="note_content"       // The key for the data object
                                                                getEditorData={handleEditorChange} // Hook to capture content
                                                                noteAdd={handleSaveNote}        // Hook for the Save button
                                                                noteAdding={isSaving}           // Loading state
                                                                show_toolbar={true}             // Show the UI
                                                                edit_id={editSessionId}                     // Change this to reset the editor (e.g., new eval session)
                                                            />

                                                        ) : (
                                                            <p>Loading editor...</p> 
                                                        )}
                                                         

                                                    </div>
                                                    {errorMsg && (
                                                        <div className="col-md-12">
                                                            <span className="text-danger">{errorMsg}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="feedback">
                                                    <div class="note-content-area">
                                                        
                                                        {/* Note List Container */}
                                                        <div className="note-list-container">
                                                        {evalNotes.data && evalNotes.data.map((item) => {
                                                            // safe lookups for user data
                                                            const user = userList[item.user_uuid] || {};
                                                            const profilePic = user.profile_pic || "https://ui-avatars.com/api/?name=User";
                                                            const userName = user.name || "Unknown User";
                                                            const profileUrl = user.profile_url || "#";
                                                            
                                                            return (
                                                            <div 
                                                                key={item.custom_note_uuid} 
                                                                className="note-card"
                                                                id={"noteId" + item.custom_note_uuid}
                                                            >
                                                                {/* Header: Avatar + Meta Info */}
                                                                <div className="note-header">
                                                                <img src={profilePic} alt={userName} className="note-avatar" />
                                                                
                                                                <div className="note-meta">
                                                                    <div className="note-author">
                                                                        <a href={profileUrl} target="_blank" rel="noopener noreferrer">
                                                                            {userName}
                                                                        </a>
                                                                    </div>
                                                                    <div className="note-info">
                                                                        <span className="note-id">#{item.custom_note_uuid}</span>
                                                                        <span className="separator1">|</span>
                                                                        <span className="note-date">
                                                                            {moment.unix(item.created_timestamp).format("D MMM YYYY, h:mm a")}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                </div>

                                                                {/* Content Area */}
                                                                <div className="note-body">
                                                                {/* Highlighted Text (Quote) */}
                                                                {item.highlight_text && (
                                                                    <div 
                                                                    className="note-highlight"
                                                                    dangerouslySetInnerHTML={{ __html: item.highlight_text }}
                                                                    />
                                                                )}

                                                                {/* Main Note Content */}
                                                                <div 
                                                                    className="note-content"
                                                                    dangerouslySetInnerHTML={{ __html: convertNoteList(item.note_content) }}
                                                                />
                                                                </div>
                                                            </div>
                                                            );
                                                        })}
                                                        </div>

                                                        {/* CSS Styles - Add this inside your render or a CSS file */}
                                                        <style>{`
                                                        .note-list-container {
                                                            display: flex;
                                                            flex-direction: column;
                                                            gap: 16px;
                                                            padding: 20px 0;
                                                        }

                                                        .note-card {
                                                            background: #fff;
                                                            border: 1px solid #eef2f5;
                                                            border-radius: 12px;
                                                            padding: 20px;
                                                            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
                                                            transition: box-shadow 0.2s ease;
                                                        }

                                                        .note-card:hover {
                                                            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                                                        }

                                                        /* Header Section */
                                                        .note-header {
                                                            display: flex;
                                                            align-items: center;
                                                            gap: 12px;
                                                            margin-bottom: 16px;
                                                        }

                                                        .note-avatar {
                                                            width: 40px;
                                                            height: 40px;
                                                            border-radius: 50%;
                                                            object-fit: cover;
                                                            border: 2px solid #fff;
                                                            box-shadow: 0 0 0 1px #e0e0e0;
                                                        }

                                                        .note-meta {
                                                            display: flex;
                                                            flex-direction: column;
                                                            line-height: 1.3;
                                                        }

                                                        .note-author a {
                                                            font-size: 15px;
                                                            font-weight: 600;
                                                            color: #2c3e50;
                                                            text-decoration: none;
                                                            transition: color 0.2s;
                                                        }
                                                        
                                                        .note-author a:hover {
                                                            color: #2c82e3;
                                                        }

                                                        .note-info {
                                                            font-size: 12px;
                                                            color: #94a3b8;
                                                            display: flex;
                                                            gap: 6px;
                                                            align-items: center;
                                                        }

                                                        .separator1 {
                                                            font-size: 8px; /* Small dot */
                                                        }

                                                        /* Content Section */
                                                        .note-body {
                                                            font-size: 14px;
                                                            color: #475569;
                                                            line-height: 1.6;
                                                        }

                                                        /* Highlight / Blockquote */
                                                        .note-highlight {
                                                            background-color: #f8fafc;
                                                            border-left: 3px solid #2c82e3;
                                                            padding: 12px 16px;
                                                            margin-bottom: 16px;
                                                            border-radius: 0 8px 8px 0;
                                                            font-style: italic;
                                                            color: #64748b;
                                                        }

                                                        .note-content a {
                                                            color: #2c82e3;
                                                            text-decoration: underline;
                                                        }
                                                        `}</style>

                                                    </div>
                                                
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-5">
                                {interactionLoading && (
                                    <div className="interaction_box">
                                        <div style={{ padding: "1.5rem", color: "#666" }}>Loading interaction details...</div>
                                    </div>
                                )}
                                {!interactionLoading && interactionError && (
                                    <div className="interaction_box">
                                        <div style={{ padding: "1.5rem" }} className="text-danger">{interactionError}</div>
                                    </div>
                                )}
                                {!interactionLoading && !interactionError && interactionData && (
                                    <div className="interaction_box">
                                        <ThinkingProcessTimeline
                                            data={interactionData}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                )}
                
            </div>
        </div>
    </div>
        
    );
};

export default EvalDetailPage;



// WEBPACK FOOTER //
// ./src/components/ai/AlieEvalDetails.js